import { Paper, Table, Typography } from '@mui/material';
import { SearchTableHeaders } from './SearchTableHeaders';
import { SearchTableBodyV2 } from './SearchTableBodyV2';
import { designAPI } from '@api/designs';
import { useSelector } from 'react-redux';
import { useAlert } from '@hooks/useAlert';
import { useState } from 'react';
import { bumpModelVersion } from '@utils/designs';
import { ConfirmModal } from '@components/dialogs/ConfirmModal';

import type { RootState } from '@features/redux/store';
import type { Coverage, Model } from '@features/redux/slices/designSlice';
import type { Dictionary } from '@/types/general';

interface DesignTableProps {
    selectDesign: (text: string) => void;
    loadModel: () => Promise<void>;
    model: Model;
    coverageMap: Dictionary<Coverage>;
}

export const DesignTable = ({ selectDesign, loadModel, model, coverageMap }: DesignTableProps) => {
    const { IdToken } = useSelector((state: RootState) => state.auth);

    const { postModel } = designAPI(IdToken as string);

    const { setAlert } = useAlert();

    const [confirmOpen, setConfirmOpen] = useState<{ id: string; name: string } | null>(null);

    const deleteDesign = async (id: string) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [id]: _, ...coverageCopy } = model?.coverages ?? {};

        const newModel: Model = {
            ...model,
            version: bumpModelVersion(model?.version),
            coverages: coverageCopy,
        };

        try {
            await postModel(newModel);

            setConfirmOpen(null);

            loadModel();

            setAlert('Design Deleted', 'success');
        } catch (error) {
            setAlert('Design did not delete successfully, contact support!', 'error');
        }
    };

    const downloadModel = async (id: string) => {
        const blob = new Blob([model.coverages[id]], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${model.model}-${coverageMap[id].coverageName}.svg`;
        a.click();
        a.remove();
    };

    return (
        <Paper>
            <Table>
                <SearchTableHeaders rows={['Design', '']} />

                <SearchTableBodyV2
                    numCols={2}
                    emptyListText={'No Designs Found'}
                    loading={false}
                    actions={[
                        {
                            color: 'error',
                            text: 'Delete',
                            onClick: (id: string) => setConfirmOpen({ id, name: coverageMap[id]?.coverageName }),
                        },
                        {
                            color: 'primary',
                            text: 'Download',
                            onClick: (id: string) => downloadModel(id),
                        },
                        {
                            color: 'primary',
                            text: 'Edit',
                            onClick: (id: string) => selectDesign(id),
                        },
                    ]}
                    rows={Object.keys(model.coverages || {}).map((modelCoverage) => {
                        const coverage = coverageMap[modelCoverage];
                        return { id: coverage?.coverageId, value: coverage?.coverageName || '' };
                    })}
                />

                <ConfirmModal
                    open={Boolean(confirmOpen)}
                    title={'Are you sure you want delete this design?'}
                    setOpen={() => setConfirmOpen(null)}
                    onCancel={() => setConfirmOpen(null)}
                    onConfirm={() => deleteDesign(confirmOpen!.id)}>
                    <Typography>
                        The design you selected <b>{confirmOpen?.name}</b> will be deleted. Are you sure you want to
                        delete?{' '}
                    </Typography>
                </ConfirmModal>
            </Table>
        </Paper>
    );
};
